/**
 * Pure capacity math for the enable/disable dialogs (NM-201, docs/48). Kept out of
 * the JSX — like `onboarding.ts`/`topic-sort.ts` — so the over/under counters and
 * the submit gates are unit-testable without a browser.
 *
 * A plan meters how many subjects an account may keep **enabled** at once, with a
 * separate cap for custom and catalog subjects (docs/45). A `null` cap is
 * unlimited. `/api/state.plan` carries the enabled counts and the caps.
 */

import type { StateResp } from '../../../api/schemas.js';

export type PlanBlock = NonNullable<StateResp['plan']>;
export type TopicKind = 'custom' | 'catalog';

/** The kind a subject meters against: catalog subjects are shared, custom are not. */
export function topicKind(topic: { catalog: boolean }): TopicKind {
  return topic.catalog ? 'catalog' : 'custom';
}

export interface KindCapacity {
  kind: TopicKind;
  used: number;
  /** null = unlimited. */
  cap: number | null;
  /** null = unlimited; else `cap - used`, floored at 0. */
  free: number | null;
}

/** This account's capacity for one kind, read from the plan block. */
export function kindCapacity(plan: PlanBlock, kind: TopicKind): KindCapacity {
  const used = kind === 'custom' ? plan.customCount : plan.catalogCount;
  const cap = kind === 'custom' ? plan.customCap : plan.catalogCap;
  return { kind, used, cap, free: cap === null ? null : Math.max(0, cap - used) };
}

/** One kind's usage plus how close it is to its cap, for the rail plan-usage line. */
export interface UsagePart extends KindCapacity {
  /** At or over the cap (a capped kind only). "Add" now means "upgrade". */
  atLimit: boolean;
  /** Capped, not yet at the cap, but 80%+ full — the "getting close" warning (NM-227). */
  nearLimit: boolean;
}

/**
 * The rail's plan-usage parts (NM-227): custom first, then catalog, each with its
 * enabled count, its cap (null = unlimited), and whether it is at / near the cap so
 * the row can flag a kind the moment it fills — not just when it is already full.
 * "Near" is 80% because the caps are small (a few to a few dozen), so a ratio reads
 * the same as "one or two left"; unlimited kinds never flag.
 */
export function planUsageParts(plan: PlanBlock): [UsagePart, UsagePart] {
  return [usagePart(kindCapacity(plan, 'custom')), usagePart(kindCapacity(plan, 'catalog'))];
}

function usagePart(c: KindCapacity): UsagePart {
  const atLimit = c.cap !== null && c.used >= c.cap;
  const nearLimit = c.cap !== null && c.cap > 0 && !atLimit && c.used / c.cap >= 0.8;
  return { ...c, atLimit, nearLimit };
}

/**
 * Whether the account can move to a pricier tier (NM-226): mock billing is on and
 * the tier table has a plan priced above the current one. Shared by the rail
 * button, the Settings upgrade section, and the `open-checkout` handler.
 */
export function canUpgrade(billing: StateResp['billing'], currentPlanId: string): boolean {
  if (!billing.enabled) return false;
  const currentPrice = billing.plans.find((p) => p.id === currentPlanId)?.priceUsd ?? 0;
  return billing.plans.some((p) => p.priceUsd > currentPrice);
}

/**
 * How many currently-enabled topics of a kind must be **disabled** to fit `adding`
 * newly-enabled ones within the cap. Zero for an unlimited kind.
 */
export function needToDisable(cap: number | null, used: number, adding: number): number {
  if (cap === null) return 0;
  return Math.max(0, used + adding - cap);
}

/** The disable dialog can submit once at least `need` topics are ticked. */
export function canSubmitDisable(pickedCount: number, need: number): boolean {
  return pickedCount >= need;
}

/** How many picked topics fall in each kind (for the enable dialog's per-kind gate). */
export function pickedByKind(picked: readonly { catalog: boolean }[]): { custom: number; catalog: number } {
  let custom = 0;
  let catalog = 0;
  for (const t of picked) {
    if (t.catalog) catalog += 1;
    else custom += 1;
  }
  return { custom, catalog };
}

/**
 * The enable (upgrade) dialog can submit when at least one topic is ticked and the
 * ticked set stays within the free capacity of **each** kind — a `null` (unlimited)
 * kind never blocks.
 */
export function canSubmitEnable(picked: readonly { catalog: boolean }[], plan: PlanBlock): boolean {
  if (picked.length === 0) return false;
  const by = pickedByKind(picked);
  const customFree = kindCapacity(plan, 'custom').free;
  const catalogFree = kindCapacity(plan, 'catalog').free;
  if (customFree !== null && by.custom > customFree) return false;
  if (catalogFree !== null && by.catalog > catalogFree) return false;
  return true;
}

/** Total room to enable more across both kinds; null when either kind is unlimited. */
export function totalFree(plan: PlanBlock): number | null {
  const c = kindCapacity(plan, 'custom').free;
  const k = kindCapacity(plan, 'catalog').free;
  if (c === null || k === null) return null;
  return c + k;
}

/**
 * Whether the upgrade prompt should offer to re-enable topics: there is at least
 * one disabled topic AND free capacity of the kind it belongs to. Unlimited counts
 * as free. Drives the once-per-session prompt after a plan upgrade.
 */
export function hasReEnableRoom(plan: PlanBlock, disabled: readonly { catalog: boolean }[]): boolean {
  if (disabled.length === 0) return false;
  const customFree = kindCapacity(plan, 'custom').free;
  const catalogFree = kindCapacity(plan, 'catalog').free;
  const anyCustom = disabled.some((t) => !t.catalog);
  const anyCatalog = disabled.some((t) => t.catalog);
  if (anyCustom && (customFree === null || customFree > 0)) return true;
  if (anyCatalog && (catalogFree === null || catalogFree > 0)) return true;
  return false;
}

/** "2 of 3 selected" style counter for the enable dialog; unlimited hides the cap. */
export function enableCounterText(pickedCount: number, plan: PlanBlock): string {
  const room = totalFree(plan);
  if (room === null) return pickedCount === 1 ? '1 selected' : `${String(pickedCount)} selected`;
  return `${String(pickedCount)} of ${String(room)} selected`;
}

/** "Disable 1 more" / "Ready — disable 2" guidance for the disable dialog. */
export function disableCounterText(pickedCount: number, need: number): string {
  const remaining = need - pickedCount;
  if (remaining > 0) return `Pick ${String(remaining)} more to disable`;
  if (pickedCount === 0) return 'Nothing to disable';
  return `Disabling ${String(pickedCount)}`;
}
